import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { combineLatest, distinctUntilChanged, filter, first, fromEvent, map, mapTo, merge, NEVER, of, startWith, switchMap, take, takeUntil, timer } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class UserCommsService {
  constructor(private http: HttpClient) { }

  registerClick(fullUrl: string) {
    const endpointUrl = `${environment.adminBaseUrl}api/user-comms/clicks`;

    // 1) Visibility stream: true when tab is visible, false when hidden.
    const visibility$ = fromEvent(document, 'visibilitychange').pipe(
      startWith(0),
      map(() => document.visibilityState === 'visible'),
      distinctUntilChanged()
    );

    // 2) Hard-cancel only when navigating away for real.
    //    - beforeunload: true navigation/close
    //    - pagehide: cancel ONLY if not persisted (i.e., not bfcache/background resume)
    const beforeUnload$ = fromEvent(window, 'beforeunload');
    const pageHideHard$ = fromEvent<PageTransitionEvent>(window, 'pagehide').pipe(
      filter((e) => !(e as PageTransitionEvent).persisted) // don't cancel if persisted == true
    );
    const hardCancel$ = merge(beforeUnload$, pageHideHard$);

    // Helper to build a fresh "first human interaction" observable each time we become visible.
    const buildInteractionOnce$ = () =>
      merge(
        fromEvent<PointerEvent>(window, 'pointerdown'),
        fromEvent<PointerEvent>(window, 'pointermove'), // counts as human interaction
        fromEvent<KeyboardEvent>(window, 'keydown'),
        fromEvent<TouchEvent>(window, 'touchstart'),
        fromEvent<Event>(window, 'scroll')
      ).pipe(
        filter((e: Event & { isTrusted?: boolean }) => e.isTrusted === true),
        first(),          // only the first real interaction
        mapTo(true)
      );

    // Helper for dwell time (suggested 2s; tune to 1500–3000 ms as needed).
    const buildDwell$ = () => timer(2000).pipe(mapTo(true));

    // When visible -> arm a fresh sequence (interaction + dwell).
    // If hidden -> pause (NEVER) and rebuild when visible again.
    const readyToPost$ = visibility$.pipe(
      switchMap((isVisible) => {
        if (!isVisible) return NEVER; // pause while hidden

        // Rebuild fresh streams on each visible window:
        const interactionOnce$ = buildInteractionOnce$();
        const dwell$ = buildDwell$();

        // Both conditions must happen while visible; take only once to trigger POST.
        return interactionOnce$.pipe(
          switchMap(() => dwell$),
          take(1)
        );
      }),
      take(1),               // register only once
      takeUntil(hardCancel$) // abort only on real navigation/close
    );

    return readyToPost$.pipe(
      switchMap(() =>
        this.http.post(endpointUrl, {
          landingUrl: fullUrl,
          ts: Date.now(),
          proof: 'human-v1'
        })
      )
    ).subscribe({
      next: () => { },
      error: (err) => console.error('UserComms click error:', err)
    });
  }

}
